import { useState, type MouseEvent as ReactMouseEvent } from 'react';
import type { LinhaGantt } from '../../api/projetos.js';
import { somarDias, diasEntre } from './timeline-utils.js';

interface BarraAtividadeProps {
  linha: LinhaGantt;
  dataInicioEixo: string;
  diaPx: number;
  onEditarDatas: (inicioPrevisto: string, fimPrevisto: string) => void;
  onClique: () => void;
}

function corBarra(linha: LinhaGantt): { fundo: string; borda: string } {
  if (linha.conflito) return { fundo: 'bg-status-ambar', borda: 'border-status-ambar' };
  if (linha.estado === 'Concluida') return { fundo: 'bg-status-verde', borda: 'border-status-verde' };
  return { fundo: 'bg-aco', borda: 'border-aco' };
}

// SPEC-UX §10.1 — tooltip customizado ao passar o mouse na barra (não usar
// `title` nativo do navegador — o mockup aprovado mostra um cartão com
// início/fim exatos, % e motivo de conflito; `LinhaGantt` hoje não traz
// responsável/código externo/horas apontadas, então o tooltip mostra só os
// campos que a API já retorna — não inventar dado que não existe).
function TooltipBarra({ linha, visivel }: { linha: LinhaGantt; visivel: boolean }) {
  if (!visivel) return null;
  return (
    <div className="pointer-events-none absolute left-0 top-[26px] z-50 w-56 rounded-lg bg-ink p-2.5 text-[11px] text-base-100 shadow-lg">
      <p className="mb-1.5 text-[12px] font-bold">{linha.atividadeNome}</p>
      <div className="flex justify-between py-0.5 text-base-100/75">
        <span>Início previsto</span>
        <span className="fonte-mono text-base-100">{linha.inicioPrevisto}</span>
      </div>
      <div className="flex justify-between py-0.5 text-base-100/75">
        <span>Fim previsto</span>
        <span className="fonte-mono text-base-100">{linha.fimPrevisto}</span>
      </div>
      <div className="flex justify-between py-0.5 text-base-100/75">
        <span>% concluído</span>
        <span className="fonte-mono text-base-100">{Math.round(linha.pctDerivado * 100)}%</span>
      </div>
      {linha.conflito && (
        <p className="mt-1.5 border-t border-base-100/20 pt-1.5 font-semibold text-status-ambar">
          ⚠ Conflito de precedência (R-ATV-8) — não bloqueia.
        </p>
      )}
    </div>
  );
}

// SPEC-UX §10.1 — barra de atividade: preenchimento = pct_derivado, cores
// por estado/conflito (nunca vermelho — semáforo exclusivo de status
// derivado real). Drag de borda (redimensionar) ou corpo (mover) edita
// datas previstas via onEditarDatas; nunca reagenda outras atividades.
export function BarraAtividade({ linha, dataInicioEixo, diaPx, onEditarDatas, onClique }: BarraAtividadeProps) {
  const [arrastando, setArrastando] = useState<null | { modo: 'mover' | 'redimensionar'; xInicial: number; inicioOriginal: string; fimOriginal: string }>(null);
  const [previa, setPrevia] = useState<{ inicio: string; fim: string } | null>(null);
  const [tooltipVisivel, setTooltipVisivel] = useState(false);
  const cores = corBarra(linha);

  if (!linha.inicioPrevisto || !linha.fimPrevisto) return null;

  const inicioAtual = previa?.inicio ?? linha.inicioPrevisto;
  const fimAtual = previa?.fim ?? linha.fimPrevisto;
  const offsetDias = diasEntre(dataInicioEixo, inicioAtual);
  const duracaoDias = Math.max(1, diasEntre(inicioAtual, fimAtual));

  function iniciarArrasto(e: ReactMouseEvent, modo: 'mover' | 'redimensionar') {
    e.stopPropagation();
    setArrastando({ modo, xInicial: e.clientX, inicioOriginal: linha.inicioPrevisto!, fimOriginal: linha.fimPrevisto! });
    setTooltipVisivel(false);

    function mover(ev: globalThis.MouseEvent) {
      const deltaPx = ev.clientX - e.clientX;
      const deltaDias = Math.round(deltaPx / diaPx);
      if (modo === 'mover') {
        setPrevia({ inicio: somarDias(linha.inicioPrevisto!, deltaDias), fim: somarDias(linha.fimPrevisto!, deltaDias) });
      } else {
        const novoFim = somarDias(linha.fimPrevisto!, deltaDias);
        if (diasEntre(linha.inicioPrevisto!, novoFim) >= 1) setPrevia({ inicio: linha.inicioPrevisto!, fim: novoFim });
      }
    }
    function soltar() {
      window.removeEventListener('mousemove', mover);
      window.removeEventListener('mouseup', soltar);
      setArrastando(null);
      setPrevia((atual) => {
        if (atual) onEditarDatas(atual.inicio, atual.fim);
        return null;
      });
    }
    window.addEventListener('mousemove', mover);
    window.addEventListener('mouseup', soltar);
  }

  return (
    <div
      className={`group absolute top-1.5 flex h-[22px] cursor-pointer items-center rounded-md border-[1.5px] ${cores.borda} bg-superficie`}
      style={{ left: offsetDias * diaPx + 2, width: duracaoDias * diaPx - 4 }}
      onClick={onClique}
      onMouseEnter={() => !arrastando && setTooltipVisivel(true)}
      onMouseLeave={() => setTooltipVisivel(false)}
    >
      <div className={`absolute inset-0 rounded-[3px] ${cores.fundo} opacity-20`} />
      <div className={`absolute inset-y-0 left-0 rounded-l-[3px] ${cores.fundo}`} style={{ width: `${linha.pctDerivado * 100}%` }} />
      {linha.estado === 'Concluida' && <span className="z-10 pl-1 text-[10px] text-white">✓</span>}
      <span className="fonte-mono absolute left-full ml-1.5 whitespace-nowrap text-[10px] text-grafite">{Math.round(linha.pctDerivado * 100)}%</span>
      {/* Alças de arrasto: corpo move, borda direita redimensiona */}
      <div className="absolute inset-0 cursor-grab" onMouseDown={(e) => iniciarArrasto(e, 'mover')} />
      <div className="absolute right-0 top-0 h-full w-1.5 cursor-ew-resize" onMouseDown={(e) => iniciarArrasto(e, 'redimensionar')} />
      <TooltipBarra linha={linha} visivel={tooltipVisivel} />
    </div>
  );
}
